/**
 * useNewSince — conta i ticket arrivati dal momento dell'ultima visita
 * per ciascuna sezione (rdi, allarme, hyper).
 *
 * Logica:
 * - localStorage["ts_seen_<section>"] = ISO timestamp ultima visita
 * - fetchRequestCount con date_from=ts → conta ticket nuovi
 * - Polling ogni 60 s
 * - markSectionSeen(section) può essere chiamato da qualsiasi componente:
 *   scrive in localStorage e dispatcha "section:seen" per aggiornamento immediato
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchRequestCount } from "../api/client";

export type Section = "rdi" | "allarme" | "hyper";
const LS_KEY = (s: string) => `ts_seen_${s}`;
const POLL_MS = 60_000;

/** Standalone: chiamare al mount della pagina per azzerare il badge. */
export function markSectionSeen(section: Section): void {
  localStorage.setItem(LS_KEY(section), new Date().toISOString());
  window.dispatchEvent(new CustomEvent("section:seen", { detail: section }));
}

export interface NewCounts {
  rdi: number;
  allarme: number;
  hyper: number;
}

export function useNewSince() {
  const [counts, setCounts] = useState<NewCounts>({ rdi: 0, allarme: 0, hyper: 0 });
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchCounts = useCallback(async () => {
    const tsSeen = (s: string) =>
      localStorage.getItem(LS_KEY(s)) ?? new Date(0).toISOString();

    const [rdi, allarme, hyper] = await Promise.all([
      fetchRequestCount({
        status: "pending_review",
        exclude_sub_type: "allarme,hyperfabbricati",
        date_from: tsSeen("rdi"),
        date_field: "created_at",
      }).catch(() => ({ count: 0 })),
      fetchRequestCount({
        status: "pending_review",
        sub_type: "allarme",
        date_from: tsSeen("allarme"),
        date_field: "created_at",
      }).catch(() => ({ count: 0 })),
      fetchRequestCount({
        status: "pending_review",
        sub_type: "hyperfabbricati",
        date_from: tsSeen("hyper"),
        date_field: "created_at",
      }).catch(() => ({ count: 0 })),
    ]);

    setCounts({ rdi: rdi.count, allarme: allarme.count, hyper: hyper.count });
  }, []);

  // Polling periodico + risposta immediata a "section:seen"
  useEffect(() => {
    fetchCounts();
    timer.current = setInterval(fetchCounts, POLL_MS);
    const onSeen = () => fetchCounts();
    window.addEventListener("section:seen", onSeen);
    return () => {
      if (timer.current) clearInterval(timer.current);
      window.removeEventListener("section:seen", onSeen);
    };
  }, [fetchCounts]);

  return { counts };
}
